/** An example order total (minor units) the processing-fee screens price each rate on. */
export const PROCESSING_FEE_EXAMPLE_TOTAL = 2500;

/**
 * Whether the partner sets its own Stripe processing-fee rates, which is what the
 * rate screens are for. Decided by the backend: the destination flag alone is on
 * for every PLG partner, most of which have no Stripe platform of their own.
 */
export function setsStripeDestinationRates(serviceProvider) {
  return serviceProvider?.stripe_destination_rates_editable === true;
}

/**
 * Whether this restaurant is held back until its partner decides the Stripe
 * processing fee (a rate, or a recorded consent to a 0% margin): it cannot go
 * live, and when live it takes no online payment. The backend sets the flag
 * only once destination charges are live and the restaurant is on Stripe;
 * checking the provider too keeps a stale company payload from blocking a
 * legacy partner.
 */
export function processingFeeBlocksRestaurant(company, serviceProvider) {
  return setsStripeDestinationRates(serviceProvider) && company?.stripe_processing_fee_rate_missing === true;
}

/**
 * Stripe's published payment rates, on the English page for the partner's
 * platform country (`/en-es/pricing`) when known.
 */
export function stripePricingUrl(country) {
  return country
    ? `https://stripe.com/en-${String(country).toLowerCase()}/pricing#payments`
    : 'https://stripe.com/pricing#payments';
}

/**
 * The worked example on the processing-fee screens. Stripe's fee is the EEA
 * standard card rate (1.5% + 0.25) for illustration only: the real one depends
 * on the card, its country and the partner's Stripe pricing.
 */
export const STRIPE_EXAMPLE_FEE = Object.freeze({ percentage: 150, fixed_fee_amount: 25 });

export const PROCESSING_FEE_EXAMPLE_RATES = Object.freeze([
  Object.freeze({ key: 'above', percentage: 300, fixed_fee_amount: 25 }),
  Object.freeze({ key: 'below', percentage: 100 }),
  Object.freeze({ key: 'none', percentage: 0 }),
]);

/**
 * The Stripe methods this manager may rate: all of them for a super admin, the
 * "any method" fallback for everyone else (the backend refuses the rest and
 * keeps whatever a super admin set).
 */
export function editableStripeMethods(methodKeys, isSuperAdmin) {
  return isSuperAdmin ? methodKeys : methodKeys.filter((method) => method === 'fallback');
}

/** Whether the book carries rates the manager can't see: method rates a super admin set. */
export function hasHiddenMethodRates(rates, editableMethods) {
  return Object.keys(rates ?? {}).some((method) => !editableMethods.includes(method));
}

/**
 * A rejected save as one message and the field messages under it. The 422
 * names fields by wire path (`rates.card.percentage`), which means nothing on
 * screen, so only the messages are kept.
 */
export function rateValidationFeedback(response, fallbackMessage) {
  const errors = response?.errors ? Object.values(response.errors).flat() : [];

  return { message: response?.message ?? fallbackMessage, errors };
}
